import {
  AfterViewChecked,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Panel } from 'primeng/panel/panel';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { SelectItem } from 'primeng';
import { Subscription } from 'rxjs';
import { EventService, MapService } from '../../../../services';
import { JobScreenCommonService } from '../../common/job-screen-common.service';
import {
  IRouteJobScreenTasks,
  IRouteJobScreenTasksTaskInfo,
} from '../../../../interfaces/screen-specific/job-screen/route-details/route-job-screen-tasks';
import { RouteRecommendationService } from '../../../../services/route-recommendation.service';

@Component({
  selector: 'route-job-screen-route-tasks',
  templateUrl: './route-job-screen-route-tasks.component.html',
  styleUrls: [
    './route-job-screen-route-tasks.component.scss',
    '../../common/job-screen-common.scss',
    '../../common/common-scss/job-screen-tasks-common.scss',
  ],
})
export class RouteJobScreenRouteTasksComponent implements OnChanges, OnDestroy, AfterViewChecked {
  @Input() routeId: number;
  @ViewChild('panel') panel: Panel;

  organisationTypeId: number = OrganisationTypeEnum.Supplier;
  selectedTaskId: number;
  selectedTask: IRouteJobScreenTasksTaskInfo;

  public taskSequencingEnabled: boolean = false;

  public get secondaryVehicleInfo(): string[] {
    if (!this.selectedTask) {
      return [];
    }

    const fields = [
      this.selectedTask.vehicle.color,
      this.selectedTask.vehicle.kerbWeight ? `${this.selectedTask.vehicle.kerbWeight}kg` : null,
      this.selectedTask.vehicle.fuelType,
    ];

    return fields.filter((x) => x != null && x != '');
  }

  public tasks: SelectItem[];

  public routeTasks: IRouteJobScreenTasks;

  routeInfo: { distanceMiles: number; time: string };
  interactiveMap: boolean = false;
  staticMapSrc: string;
  mapWidth: number;
  mapHeight: number = 398;

  subscriptions: Subscription[] = [];

  private mapUrlNeedsGenerating: boolean = false;

  constructor(
    private eventService: EventService,
    public commonService: JobScreenCommonService,
    private routeRecommendationService: RouteRecommendationService,
    private mapService: MapService,
  ) {
    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event) => {
        this.handleEvents(event);
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.routeId && this.routeId != null) {
      this.selectedTaskId = null;
      void this.fetchRoute();
    }
  }

  handleEvents(event: any) {
    if (event.event == 'task-changed') {
      this.selectedTaskId = event.data.taskId;
      this.updateSelectedTask();
    }

    if (event.event == 'refresh-job') {
      void this.fetchRoute();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  ngAfterViewChecked(): void {
    if (this.mapUrlNeedsGenerating && this.panel) {
      const panelWidth = (this.panel as any).el.nativeElement.offsetWidth;

      if (panelWidth > 0) {
        this.staticMapSrc = this.generateStaticMapImageURL(this.selectedTask.id);

        this.mapUrlNeedsGenerating = false;
      }
    }
  }

  changeSelectedTask(taskId: number) {
    this.eventService.JobScreenEvent.emit({
      event: 'task-changed',
      data: { taskId, routeId: this.routeTasks.routeId },
      sender: 'changeSelectedTask',
    });
  }

  updateSelectedTask() {
    this.selectedTask = this.routeTasks.tasks.find((o) => o.id == this.selectedTaskId);

    if (this.selectedTask == null) {
      return;
    }

    this.staticMapSrc = null;
    this.mapUrlNeedsGenerating = true;

    this.updateRouteInfo();
  }

  generateStaticMapImageURL(taskId: number): string {
    const mapWidth = Math.round((this.panel as any).el.nativeElement.offsetWidth * 0.4);
    this.mapWidth = mapWidth;

    const endpoint = `/api/map/layeredStaticMapImage`;
    const params = new URLSearchParams();

    params.set('mapSize', `${mapWidth}x${this.mapHeight}`);
    params.set('routeId', this.routeId.toString());
    params.set('primaryTaskId', taskId.toString());

    return `${endpoint}?${params.toString()}`;
  }

  refreshRoute() {
    this.tasks = [];

    if (this.routeTasks) {
      this.routeTasks.tasks.forEach((item) => {
        this.tasks.push({ label: item.vehicle.regNumber, value: item });
      });

      this.selectedTask = this.routeTasks.tasks.find((o) => o.id == this.selectedTaskId);
      if (this.selectedTaskId == null) {
        this.changeSelectedTask(this.routeTasks.tasks[0].id);
      }

      this.taskSequencingEnabled = this.routeTasks.tasks.some((task) => task.taskSequence != null);
    }
  }

  updateRouteInfo() {
    this.routeInfo = { distanceMiles: null, time: null };

    if (this.selectedTask.estimateTime) {
      this.routeInfo.time = this.mapService.getTravelTimeFormatted(this.selectedTask.estimateTime);
      this.routeInfo.distanceMiles = this.mapService.getMiles(this.selectedTask.distance);
    }
  }

  async fetchRoute() {
    this.routeTasks = await this.routeRecommendationService.getRouteJobScreenTasks(this.routeId);
    console.log('route loaded', this.routeTasks);
    this.refreshRoute();
  }
}
